/**
 * The words the two daily cards (Home's, and the Daily tab's) say about the
 * same facts, so they cannot say different things. Pure, so it runs under
 * plain Vitest (`test/state/daily-view.test.ts`).
 */
import { formatClock } from '@interlock/modes';

import type { DailyInfo } from './daily-io.js';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/**
 * "2 October" from the server's `YYYY-MM-DD`. Read as text, never through
 * `Date`: the date is the server's day, and parsing it in this device's time
 * zone could show yesterday's.
 */
export function dailyDayLabel(date: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (match === null) return date;
  const month = MONTHS[Number(match[2]) - 1];
  if (month === undefined) return date;
  return `${String(Number(match[3]))} ${month}`;
}

/** "Sprint to 40 lines": what the daily is, whoever is asking. */
export function dailyGoalLabel(info: Pick<DailyInfo, 'goalLines'>): string {
  return `Sprint to ${String(info.goalLines)} lines`;
}

/**
 * Where the player stands today: "you placed #4 · 1:42.3", or "not played
 * yet". `finishedHere` is a guest's own finish on this device, which the
 * server never hears about.
 */
export function dailyStanding(
  info: Pick<DailyInfo, 'played' | 'yourRank' | 'yourResult'>,
  finishedHere: boolean,
): string {
  if (info.played && info.yourRank !== null && info.yourResult !== null) {
    return `you placed #${String(info.yourRank)} · ${formatClock(info.yourResult)}`;
  }
  return finishedHere ? 'played, not posted' : 'not played yet';
}

/** Whether today's daily is still waiting to be played: the tab bar's dot. */
export function dailyIsWaiting(
  info: Pick<DailyInfo, 'played' | 'date'> | null,
  finishedHereDate: string | null,
): boolean {
  if (info === null) return false;
  return !info.played && finishedHereDate !== info.date;
}
